import { createContext, useContext } from 'react';
import { load, save } from './lib';

export type Lang = 'cs' | 'en';

const LANG = 'bingo:lang';

function isLang(v: unknown): v is Lang {
  return v === 'cs' || v === 'en';
}

export function detectLang(): Lang {
  const fromUrl = new URLSearchParams(location.search).get('lang');
  if (isLang(fromUrl)) return fromUrl;
  const saved = load<unknown>(LANG, null);
  if (isLang(saved)) return saved;
  const nav = (navigator.languages?.[0] || navigator.language || '').toLowerCase();
  return nav.startsWith('cs') || nav.startsWith('sk') ? 'cs' : 'en';
}

export function rememberLang(lang: Lang): void {
  save(LANG, lang);
  const url = new URL(location.href);
  url.searchParams.set('lang', lang);
  history.replaceState(null, '', url);
}

function csPlural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}

const cs = {
  switchTo: 'EN',
  switchLabel: 'Switch to English',
  newBingo: '+ Nové bingo',
  heroA: 'Bingo na',
  heroB: 'cokoliv',
  lead: 'Napiš políčka, pošli odkaz kamarádům a odškrtávejte spolu. Volby, porady, rodinné oslavy — bez registrace a každý hráč může dostat svou vlastní zamíchanou kartu.',
  create: 'Vytvořit vlastní bingo',
  playHero: 'Zahrát si volební',
  playHeroLabel: 'Zahrát si volební bingo',
  recent: 'Naposledy hrané',
  untitled: 'Bingo bez názvu',
  play: 'Hrát',
  edit: 'Upravit',
  remove: 'Odebrat',
  removeLabel: (t: string) => `Odebrat ${t} ze seznamu`,
  inspiration: 'Inspirace',
  editOwn: 'Upravit podle sebe',
  playLabel: (t: string) => `Hrát ${t}`,
  how: 'Jak to funguje',
  steps: [
    ['Napiš políčka.', 'Co se může stát? Každý řádek je jedno políčko, karta může být 3 × 3, 4 × 4 nebo 5 × 5.'],
    ['Pošli odkaz.', 'Celé bingo je uložené v odkazu — nikdo se nemusí registrovat.'],
    ['Odškrtávej.', 'Když se to stane, klikni. Celá řada, sloupec nebo úhlopříčka = BINGO!'],
  ] as [string, string][],
  editorNew: 'Nové bingo',
  editorEdit: 'Uprav bingo',
  editingCopy: 'Upravuješ kopii — původní odkaz zůstane, jak byl.',
  title: 'Název',
  titlePh: 'Třeba Volební bingo',
  size: 'Velikost karty',
  cells: 'Políčka',
  cellsHint: '— každý řádek jedno',
  cellsPh: 'Někdo přijde pozdě\n„Slyšíte mě?“\nPes v pozadí\n…',
  stEmpty: (need: number) => `Napiš aspoň ${need} políček — každý řádek je jedno.`,
  stMissing: (missing: number, have: number, need: number) =>
    `Chybí ještě ${missing} ${csPlural(missing, 'políčko', 'políčka', 'políček')} (máš ${have} z ${need}).`,
  stMoreShuffle: (need: number, have: number) => `Každý hráč dostane náhodných ${need} z ${have} políček.`,
  stMoreFixed: (need: number) => `Použije se prvních ${need} políček. Zapni míchání, ať se střídají všechna.`,
  stExact: (need: number) => `Přesně ${need} políček. Můžeš hrát!`,
  shuffle: 'Každý hráč dostane zamíchanou kartu',
  shuffleHint: 'Stejná políčka, jiné pořadí. Když jich napíšeš víc, každý dostane jiný výběr.',
  free: 'Volné pole uprostřed',
  freeHintOdd: 'Prostřední políčko je odškrtnuté hned od začátku.',
  freeHintEven: 'Jen u karty 3 × 3 nebo 5 × 5.',
  share: 'Sdílet odkaz',
  preview: 'Náhled',
  previewLabel: 'Náhled karty',
  freeCell: 'Volné',
  defaultTitle: 'Bingo',
  invite: 'Pozvat hráče',
  newCard: 'Nová karta',
  clear: 'Vymazat značky',
  print: 'Vytisknout',
  editCopy: 'Upravit kopii',
  noteShuffle: 'Každý, kdo otevře odkaz, má vlastní zamíchanou kartu.',
  noteSame: 'Všichni hráči mají stejnou kartu.',
  noteMemory: 'Odškrtnutá políčka si pamatuje tento prohlížeč.',
  stampBingo: 'BINGO!',
  stampFull: 'PLNÁ KARTA!',
  shareTitle: 'Pošli odkaz hráčům',
  shareShuffle: 'Každý, kdo odkaz otevře, dostane svou vlastní zamíchanou kartu.',
  shareSame: 'Všichni, kdo odkaz otevřou, dostanou stejnou kartu.',
  shareInLink: 'Celé bingo je uložené přímo v odkazu.',
  linkLabel: 'Odkaz na bingo',
  copy: 'Kopírovat',
  copied: 'Zkopírováno ✓',
  shareNative: 'Sdílet…',
  close: 'Zavřít',
  badTitle: 'Tenhle odkaz nefunguje',
  badLead: 'Odkaz na bingo je nejspíš useknutý — zkus si ho nechat poslat znovu celý, nebo si udělej vlastní.',
  badCreate: 'Vytvořit bingo',
  home: 'Na úvod',
  footer: 'Nadrobeno na',
  docHome: 'Bingo — udělej si vlastní',
  docEditor: 'Nové bingo — Bingo',
};

export type Strings = typeof cs;

const en: Strings = {
  switchTo: 'CZ',
  switchLabel: 'Přepnout do češtiny',
  newBingo: '+ New bingo',
  heroA: 'Bingo for',
  heroB: 'anything',
  lead: 'Write the squares, send the link to your friends and mark them off together. Elections, meetings, family dinners — no sign-up, and every player can get their own shuffled card.',
  create: 'Make your own bingo',
  playHero: 'Play polling station',
  playHeroLabel: 'Play polling station bingo',
  recent: 'Recently played',
  untitled: 'Untitled bingo',
  play: 'Play',
  edit: 'Edit',
  remove: 'Remove',
  removeLabel: (t: string) => `Remove ${t} from the list`,
  inspiration: 'Ideas',
  editOwn: 'Make it yours',
  playLabel: (t: string) => `Play ${t}`,
  how: 'How it works',
  steps: [
    ['Write the squares.', 'What might happen? Each line is one square; the card can be 3 × 3, 4 × 4 or 5 × 5.'],
    ['Send the link.', 'The whole bingo lives in the link — nobody has to sign up.'],
    ['Mark them off.', 'When it happens, tap it. A full row, column or diagonal = BINGO!'],
  ],
  editorNew: 'New bingo',
  editorEdit: 'Edit bingo',
  editingCopy: 'You are editing a copy — the original link stays as it was.',
  title: 'Title',
  titlePh: 'e.g. Meeting bingo',
  size: 'Card size',
  cells: 'Squares',
  cellsHint: '— one per line',
  cellsPh: 'Someone joins late\n“Can you hear me?”\nDog barking in the background\n…',
  stEmpty: (need: number) => `Write at least ${need} squares — one per line.`,
  stMissing: (missing: number, have: number, need: number) =>
    `${missing} more ${missing === 1 ? 'square' : 'squares'} to go (${have} of ${need}).`,
  stMoreShuffle: (need: number, have: number) => `Every player gets a random ${need} of your ${have} squares.`,
  stMoreFixed: (need: number) => `Only the first ${need} squares are used. Turn on shuffling so they all get a turn.`,
  stExact: (need: number) => `Exactly ${need} squares. Ready to play!`,
  shuffle: 'Every player gets a shuffled card',
  shuffleHint: 'Same squares, different order. Write more than fit and everyone gets a different pick.',
  free: 'Free square in the middle',
  freeHintOdd: 'The centre square is marked from the start.',
  freeHintEven: 'Only on a 3 × 3 or 5 × 5 card.',
  share: 'Share link',
  preview: 'Preview',
  previewLabel: 'Card preview',
  freeCell: 'Free',
  defaultTitle: 'Bingo',
  invite: 'Invite players',
  newCard: 'New card',
  clear: 'Clear marks',
  print: 'Print',
  editCopy: 'Edit a copy',
  noteShuffle: 'Everyone who opens the link gets their own shuffled card.',
  noteSame: 'All players have the same card.',
  noteMemory: 'This browser remembers your marks.',
  stampBingo: 'BINGO!',
  stampFull: 'FULL CARD!',
  shareTitle: 'Send the link to your players',
  shareShuffle: 'Everyone who opens it gets their own shuffled card.',
  shareSame: 'Everyone who opens it gets the same card.',
  shareInLink: 'The whole bingo is stored in the link itself.',
  linkLabel: 'Bingo link',
  copy: 'Copy',
  copied: 'Copied ✓',
  shareNative: 'Share…',
  close: 'Close',
  badTitle: 'This link does not work',
  badLead: 'The bingo link is probably cut off — ask for the whole link again, or make your own.',
  badCreate: 'Make a bingo',
  home: 'Home',
  footer: 'Made on',
  docHome: 'Bingo — make your own',
  docEditor: 'New bingo — Bingo',
};

export const STRINGS: Record<Lang, Strings> = { cs, en };

export const LangContext = createContext<{ lang: Lang; t: Strings }>({ lang: 'cs', t: cs });

export function useT() {
  return useContext(LangContext);
}
